import { Fields } from '@engoo/view-model';
import { DESERIALIZATION_LOCK_FAILURE, DESERIALIZATION_NOT_FOUND } from 'app/constants/ServerErrors';
import ApplicationError from 'app/models/request/ApplicationError';
import {
  deepSerializeEdits,
  savedToServer,
  serializeEdits,
} from 'app/remote';
import { beginSave, saveFailed } from 'app/remote/generic/actions';
import vmClient from 'app/services/vmClient';
import { Record, Set } from 'immutable';

// base.js is needed to avoid having a circular relationship between index.js
// and next.ts. The values exported here are things that are used in next.ts
// (but don't have types yet so they can't be in next.ts itself), and also need
// to be exported from index.js

const Ref = Record({ type: null, id: null });

export class RetryLimitExceeded {
  constructor(details) {
    this.message = `Retry limit exceeded while saving ${JSON.stringify(details.savingRoots)}` +
      `: ${details.exception}`;
  }

  toString() {
    return this.message;
  }
}

export const errorRecoverable = (savingRoots, error) => {
  if (error.code === DESERIALIZATION_LOCK_FAILURE) {
    // optimistic lock failure, will be fine when we update from the server
    return true;
  }

  // need to identify what couldn't be found when it's a 404, since if it's the
  // lesson, then someone deleted it from under us, while if it's a lesson body
  // entity, it means we're stale (great), while if it's a shared reference,
  // then we're sad again.

  // TODO what happens when someone deletes a shared_text we want to use. That's
  // a misery. Will probabably need more context than savingLessonID to
  // determine what's a shared and what's the contents and what's the root.

  if (error.code === DESERIALIZATION_NOT_FOUND) {
    const refIsRoot = (ref) =>
    (savingRoots.has(ref)) || vmClient.isTypeShared(ref[Fields.TYPE]);
    return !error.meta.nodes.some(refIsRoot);
  }

  // anything else, bail
  return false;
};

export const recoverable = (savingRoots, failure) => (
  failure instanceof ApplicationError &&
  failure.causes.every((e) => errorRecoverable(savingRoots, e))
);

/**
 * Factory for creating a single save action with a configurable `getEdits` strategy.
 * @param getEdits - How to serialze an entities edits
 * @returns a function for creating a save once action.
 *
 * All of the save logic between saving deeply or shallowly serialized edits is the same.
 */
const makeSaveEntityOnce = (getEdits) => (postEntity, type, id) => (dispatch, getState) => {
  const edit = getEdits(getState(), type, id);

  dispatch(beginSave());

  if (!edit.data) {
    // All actions have been saved to the server (actually there was nothing to
    // save, so we don't need to send a request). Send a message for the benefit
    // of the action log.
    dispatch(savedToServer());
    return Promise.resolve();
  }

  try {
    return dispatch(postEntity(edit))
      .catch((e) => {
        dispatch(saveFailed());
        throw e;
      })
      .then((result) => {
        dispatch(savedToServer(result, type, id));
        return result;
      });
  } catch (e) {
    dispatch(saveFailed());
    throw e;
  }
};


/**
 * The action to save an entity one time.
 *
 * This is called repeatedly when a save action attempts to recover after an error
 * during the save process. Note that SHARED associations are NOT saved as part of
 * this action (but nested entities are).
 * @param {function} postEntity
 * @param {function} type
 * @param {string} id
 * @returns {Promise}
 */
export const saveEntityOnce = makeSaveEntityOnce(serializeEdits);

/**
 * Deeply save an entity one time.
 *
 * This is also used by a version of the save entity action. It is called repeatedly
 * when there are errors to attempt to recover. Notably, this verison will attempt to
 * save the entity and all shared associations deeply. It is primarily meant to be used
 * with external associations.
 *
 * @param {function} postEntity
 * @param {function} type
 * @param {string} id
 * @returns {Promise}
 */
export const saveEntityOnceDeep = makeSaveEntityOnce(deepSerializeEdits);

/**
 * A helper for creating a save entity action.
 * @params saveOnceAction - How to save the action one time.
 *
 * The majority of the logic for saving (and attempting to recover from failed saves)
 * is identical whether or not we are saving shallowly or deeply.
 */
const makeSaveEntity = (saveOnceAction) => (fetch, postEntity, type) => id => {
  const { typeName } = type;
  const savingRoots = Set.of(Ref({ type: typeName, id }));

  // When we hit a recoverable error while saving, we'll retry immediately. If
  // for some reason this fails again, this becomes a very rapid loop hammering
  // the server. If we loop too much, there's probably a bigger failure, such as
  // a client bug, and we should propagate an error upwards.
  let retry = true;

  const doSave = (dispatch, getState) =>
    dispatch(saveOnceAction(postEntity, type, id)).catch((e) => {
      if (recoverable(savingRoots, e)) {
        if (retry) {
          retry = false;
          return dispatch(fetch(id)).then(() => dispatch(doSave));
        } else {
          throw new RetryLimitExceeded({ savingRoots, exception: e });
        }
      } else {
        throw e;
      }
    });
  return doSave;
};

/**
 * Shallowly save an entity.
 *
 * Shallowly meaning that associations will not be saved.
 * @param fetch - how to get an updated version of the entity.
 * @param postEntity - how to save the entity.
 * @param type - the type of the entity being saved.
 * @returns as dispatchable action.
 */
export const saveEntity = makeSaveEntity(saveEntityOnce);
/**
 * Entity action for deeply saving an entity.
 *
 * Deeply saving means that the entity and all of its associations will be saved at once.
 *
 * @param fetch - how to get an updated version of the entity after saving.
 * @param postEntity - how  to save the entity
 * @param type - The type of the entity being saved
 * @returns a dispatchable action
 */
export const saveEntityDeep = makeSaveEntity(saveEntityOnceDeep);
